import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  Smartphone, Plus, Search, Trash2, PowerOff, QrCode,
  Flame, Signal, Edit, Zap, AlertTriangle, UserCheck, Infinity, CheckCircle2,
} from "lucide-react";
import { useInstances } from "@/hooks/useInstances";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { FlagImage, defaultCountries, guessCountryByPartialPhoneNumber } from "react-international-phone";

// ── Effective days calculation (same as Dashboard) ──
function calculateEffectiveDays(inst: {
  total_active_seconds: number | null;
  profile_active_seconds: number | null;
  last_connected_at: string | null;
  evolution_connection_state: string | null;
}) {
  const isOnline = inst.evolution_connection_state === "open";
  const liveSeconds =
    isOnline && inst.last_connected_at
      ? (Date.now() - new Date(inst.last_connected_at).getTime()) / 1000
      : 0;
  const totalEffectiveDays = Math.floor(((inst.total_active_seconds || 0) + liveSeconds) / 86400);
  const phaseDays = Math.floor(((inst.profile_active_seconds || 0) + liveSeconds) / 86400);
  return { totalEffectiveDays, phaseDays };
}

function formatPhone(phone: string) {
  const clean = phone.replace(/\D/g, "");
  if (clean.length === 13) return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 9)}-${clean.slice(9)}`;
  if (clean.length === 12) return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 8)}-${clean.slice(8)}`;
  return phone;
}

export default function DashboardNumbers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { instances, userProfile, isLoading, disconnectInstance } = useInstances();

  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // Fetch daily metrics for today
  const dailyMetricsQuery = useQuery({
    queryKey: ["daily_metrics_today", user?.id],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const instanceIds = instances.map((i) => i.id);
      if (instanceIds.length === 0) return [];
      const { data, error } = await supabase
        .from("daily_metrics")
        .select("instance_id, messages_sent")
        .in("instance_id", instanceIds)
        .eq("metric_date", today);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && instances.length > 0,
  });

  const dailySentMap = new Map<string, number>();
  for (const m of dailyMetricsQuery.data || []) {
    dailySentMap.set(m.instance_id, m.messages_sent || 0);
  }

  const filtered = instances.filter(
    (n) => n.phone_number.includes(search) || ((n as any).nickname || "").toLowerCase().includes(search.toLowerCase())
  );

  const onlineInstances = instances.filter((n) => n.evolution_connection_state === "open");
  const online = onlineInstances.length;
  const maxPorts = userProfile?.max_ports || 2;
  const hasGlobalNetwork = instances.some((n) => n.is_global_network_active);
  const needsSecondNumber = online === 1 && !hasGlobalNetwork;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Números</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie seus chips conectados · {instances.length}/{maxPorts} portas usadas.
          </p>
        </div>
        {instances.length >= maxPorts ? (
          <Button className="gap-2" variant="outline" onClick={() => navigate("/dashboard/upgrade")}>
            <Plus className="h-4 w-4" />
            Contratar mais números
          </Button>
        ) : (
          <Button className="gap-2 glow-orange-sm" onClick={() => navigate("/dashboard/numbers/add")}>
            <Plus className="h-4 w-4" />
            Conectar Número
          </Button>
        )}
      </div>

      {/* Warning: needs 2nd number */}
      {needsSecondNumber && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="w-9 h-9 rounded-lg bg-yellow-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <Zap className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Aquecimento pausado — conecte um 2º número</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sem a Rede Global ativa, o aquecimento funciona entre os seus próprios chips. Conecte pelo menos 2 números para que eles troquem mensagens entre si.
                </p>
              </div>
              <Button size="sm" className="shrink-0 gap-1 glow-orange-sm" onClick={() => navigate("/dashboard/numbers/add")} disabled={instances.length >= maxPorts}>
                <Plus className="h-3.5 w-3.5" /> Conectar 2º
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Warning: contacts not saved */}
      {instances.some((n) => n.evolution_connection_state === "open" && !(n as any).contacts_saved) && instances.filter(n => n.evolution_connection_state === "open").length >= 2 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-red-500/40 bg-red-500/10">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="w-9 h-9 rounded-lg bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">⚠️ Contatos não salvos na agenda!</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Detectamos que um ou mais chips <strong className="text-foreground">não têm os outros números salvos na agenda do WhatsApp</strong>. Isso prejudica gravemente o aquecimento. <strong className="text-red-400">Salve todos os chips na agenda um do outro agora!</strong>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tip: save contacts (when all saved) */}
      {online >= 2 && !instances.some((n) => n.evolution_connection_state === "open" && !(n as any).contacts_saved) && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-accent/20 bg-accent/5">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                <UserCheck className="h-5 w-5 text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">✅ Contatos salvos entre si</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Seus chips estão com os contatos salvos corretamente. Isso maximiza a eficácia do aquecimento.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Total de Números", value: `${instances.length}/${maxPorts}`, icon: Smartphone, accent: "text-primary" },
          { label: "Online Agora", value: `${online}/${instances.length}`, icon: Signal, accent: "text-accent" },
          { label: "Score Médio", value: instances.length ? Math.round(instances.reduce((a, b) => a + (b.health_score || 0), 0) / instances.length) : 0, icon: Flame, accent: "text-orange-400" },
        ].map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                  <c.icon className={`h-5 w-5 ${c.accent}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="font-display text-xl font-bold text-foreground">
                    {isLoading ? <Skeleton className="h-6 w-12" /> : c.value}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por telefone ou apelido..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-secondary/40 border-warmz-glass-border" />
      </div>

      {/* Table */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead>Número</TableHead>
                    <TableHead>Apelido</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Dias Online</TableHead>
                    <TableHead>Aquecimento</TableHead>
                    <TableHead>Msgs Hoje</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-5 w-20" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        {instances.length === 0 ? "Nenhum número conectado. Clique em \"Conectar Número\" para começar." : "Nenhum resultado encontrado."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((inst, i) => {
                      const isOnline = inst.evolution_connection_state === "open";
                      const profile = inst.warmup_profiles;
                      const { totalEffectiveDays, phaseDays } = calculateEffectiveDays(inst);
                      const durationDays = profile?.duration_days;
                      const progressPct = durationDays
                        ? Math.min(100, Math.round((phaseDays / durationDays) * 100))
                        : 100;
                      const isInfinite = !durationDays;
                      const profileIcon = profile?.ui_icon || "❄️";
                      const profileName = profile?.name?.split(" / ")[0] || "Frio";

                      const sentToday = dailySentMap.get(inst.id) || 0;
                      const maxDaily = profile?.max_daily_msgs || 0;
                      const dailyPct = maxDaily > 0 ? Math.min(100, Math.round((sentToday / maxDaily) * 100)) : 0;
                      const dailyComplete = maxDaily > 0 && sentToday >= maxDaily;

                      return (
                        <motion.tr
                          key={inst.id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + i * 0.04 }}
                          className="border-b border-warmz-glass-border/20 hover:bg-warmz-card-hover/40 transition-colors cursor-pointer"
                          onClick={() => navigate(`/dashboard/numbers/${inst.id}/edit`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FlagImage iso2={guessCountryByPartialPhoneNumber({ phone: "+" + inst.phone_number.replace(/\D/g, ""), countries: defaultCountries }).country?.iso2 || "br"} size="18px" />
                              <span className="font-mono text-sm">{formatPhone(inst.phone_number)}</span>
                              {isOnline && !(inst as any).contacts_saved && (
                                <span title="Contato não salvo pelos outros chips!" className="flex items-center gap-0.5 text-red-400">
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                </span>
                              )}
                              {isOnline && (inst as any).contacts_saved && (
                                <span title="Contato salvo ✓" className="text-accent">
                                  <UserCheck className="h-3.5 w-3.5" />
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-foreground">
                              {(inst as any).nickname || <span className="text-muted-foreground italic">—</span>}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {isOnline && needsSecondNumber ? (
                                <>
                                  <div className="w-2 h-2 rounded-full bg-yellow-400" />
                                  <span className="text-xs font-medium text-yellow-400">Pausado</span>
                                </>
                              ) : (
                                <>
                                  <div className={`w-2 h-2 rounded-full ${isOnline ? "bg-accent animate-pulse" : "bg-destructive"}`} />
                                  <span className={`text-xs font-medium ${isOnline ? "text-accent" : "text-destructive"}`}>
                                    {isOnline ? "Online" : "Offline"}
                                  </span>
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <span className="text-sm text-foreground font-medium">{totalEffectiveDays} dias (efetivos)</span>
                          </TableCell>
                          <TableCell>
                            <div className="min-w-[200px] space-y-1">
                              <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                                <span>{profileIcon}</span>
                                <span>{profileName}</span>
                                {isInfinite ? (
                                  <span className="flex items-center gap-0.5 text-muted-foreground ml-1">
                                    <Infinity className="h-3 w-3" />
                                    <span>Operação Contínua</span>
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground ml-1">
                                    ({phaseDays}/{durationDays} dias)
                                  </span>
                                )}
                              </div>
                              {isInfinite ? (
                                <div className="h-2 rounded-full bg-gradient-to-r from-accent to-yellow-400 w-full" />
                              ) : (
                                <Progress value={progressPct} className="h-2" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="min-w-[100px] space-y-1">
                              <div className="flex items-center gap-1 text-xs">
                                {dailyComplete ? (
                                  <span className="flex items-center gap-1 text-accent font-medium">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Completo
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground font-medium">
                                    {sentToday}/{maxDaily}
                                  </span>
                                )}
                              </div>
                              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${dailyComplete ? "bg-accent" : "bg-primary"}`}
                                  style={{ width: `${dailyPct}%` }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Editar" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/numbers/${inst.id}/edit`); }}>
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              {isOnline ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  title="Desconectar"
                                  onClick={(e) => { e.stopPropagation(); disconnectInstance.mutate({ instance_id: inst.id, action: "disconnect" }); }}
                                  disabled={disconnectInstance.isPending}
                                >
                                  <PowerOff className="h-3.5 w-3.5" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-accent hover:text-accent hover:bg-accent/10"
                                  title="Reconectar"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/numbers/${inst.id}/edit`); }}
                                >
                                  <QrCode className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:text-destructive hover:bg-destructive/10"
                                title="Remover"
                                onClick={(e) => { e.stopPropagation(); setDeleteTarget(inst.id); }}
                                disabled={disconnectInstance.isPending}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </motion.tr>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Confirm delete dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir número?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação é irreversível. O número será desconectado e removido permanentemente do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) disconnectInstance.mutate({ instance_id: deleteTarget, action: "delete" });
                setDeleteTarget(null);
              }}
            >
              Sim, excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
